import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addManualAccess, listBranchModules, listManualAccess, withdrawManualAccess } from '@/api/branchRelationships';
import { getLocations, getRegions } from '@/api/locations';
import { useAuth } from '@/auth/useAuth';
import { PersonPicker } from '@/components/common/assignPickers';
import {
  CoverageHint, CoveragePanel, ResizablePalette, SelectRow, type CoverageItem,
} from '@/components/common/allocationPanel';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { formatDate } from '@/lib/dateFormat';
import type { ManualAccess } from '@/types/oscar';
import {
  describeManualScope,
  emptyGrant,
  grantSchema,
  manualStatus,
  scopeKey,
  toGrantPayload,
  type GrantFormData,
  type GrantTarget,
  type ManualStatus,
} from './schema';
import { ErrorBanner, FieldError } from './shared';
import { btnSecondary, inputCls } from './styles';

type View = 'person' | 'list';

const statusCls: Record<ManualStatus, string> = {
  Live: 'bg-green/15 text-green',
  'Not yet': 'bg-navy/10 text-navy',
  Expired: 'bg-orange/15 text-orange',
  Withdrawn: 'bg-darkGrey/10 text-darkGrey/70',
};

/**
 * Manual access — oscar.PersonBranchRelationship: exceptions, N- people,
 * pilots. As an allocation board: pick a person and a module, and the right
 * side splits Company, every region and every branch into Allocated / Not
 * allocated. Allocate asks why (a reason is required) and for how long.
 *
 * ⚠ A row only WIDENS a person's access; it never takes any away.
 *
 * ⚠ Cost centres such as 1102 (IT) are not Oscar locations, so they are not in
 * the branch list until somebody has one — "Add a cost-centre code" covers the
 * first time.
 *
 * "All rows" keeps the flat list: every person at once, with reasons, dates
 * and the withdrawn rows.
 */
export function ManualAccessTab() {
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const [view, setView] = useState<View>('person');
  const [person, setPerson] = useState<{ id: number; name: string } | null>(null);
  const [moduleKey, setModuleKey] = useState('');
  const [leftQuery, setLeftQuery] = useState('');
  const [granting, setGranting] = useState<GrantTarget | null>(null);
  const [withdrawing, setWithdrawing] = useState<ManualAccess | null>(null);
  const [error, setError] = useState<string | null>(null);

  const manualQuery = useQuery({ queryKey: ['branch-rel', 'manual'], queryFn: ({ signal }) => listManualAccess(true, signal) });
  const modulesQuery = useQuery({ queryKey: ['branch-rel', 'modules'], queryFn: ({ signal }) => listBranchModules(signal) });
  const regionsQuery = useQuery({ queryKey: ['regions'], queryFn: ({ signal }) => getRegions(signal) });
  const locationsQuery = useQuery({
    queryKey: ['locations', { activeOnly: true }],
    queryFn: ({ signal }) => getLocations({ activeOnly: true }, signal),
  });

  const rows = useMemo(() => manualQuery.data ?? [], [manualQuery.data]);
  const modules = useMemo(() => modulesQuery.data ?? [], [modulesQuery.data]);

  // Default to the first module once they load, so the panel is never empty for no reason.
  useEffect(() => {
    if (!moduleKey && modules.length > 0) setModuleKey(modules[0].ModuleKey);
  }, [moduleKey, modules]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['branch-rel'] });
  const withdraw = useMutation({
    mutationFn: withdrawManualAccess,
    onSuccess: () => { setError(null); void invalidate(); },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not withdraw the access.'),
  });

  // ── Left: people who already have manual access ─────────────────────────────
  const people = useMemo(() => {
    const map = new Map<number, { id: number; name: string; email: string; count: number }>();
    for (const r of rows) {
      if (r.IsDeleted) continue;
      const p = map.get(r.PersonId) ?? { id: r.PersonId, name: r.DisplayName, email: r.PrimaryEmail, count: 0 };
      p.count += 1;
      map.set(r.PersonId, p);
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);
  const term = leftQuery.trim().toLowerCase();
  const visiblePeople = people.filter((p) => !term || `${p.name} ${p.email}`.toLowerCase().includes(term));

  // ── Right: Company, every region, every branch — for this person + module ───
  const { items, targets } = useMemo(() => {
    const targets = new Map<string, GrantTarget>();
    const items: CoverageItem<ManualAccess>[] = [];
    if (!person || !moduleKey) return { items, targets };

    const live = new Map(rows
      .filter((r) => !r.IsDeleted && r.PersonId === person.id && r.ModuleKey === moduleKey)
      .map((r) => [scopeKey(r), r] as const));
    const push = (key: string, target: GrantTarget, code: string, label: string, mono: boolean) => {
      targets.set(key, target);
      const alloc = live.get(key) ?? null;
      const status = alloc ? manualStatus(alloc) : null;
      items.push({
        key, code, label, mono,
        badge: status && status !== 'Live'
          ? <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${statusCls[status]}`}>{status}</span>
          : undefined,
        search: `${code} ${label}`.toLowerCase(),
        alloc,
        targetId: 0,
      });
    };

    push('company', { ScopeType: 'Company' }, 'Company', 'every branch', false);
    for (const g of regionsQuery.data ?? [])
      push(`region:${g.RegionId}`, { ScopeType: 'Region', RegionId: g.RegionId, RegionDesc: g.RegionDesc }, g.RegionDesc, 'region — every branch in it', false);

    // One row per branch code: Oscar locations first, then any code this person
    // already holds that is not one (a cost centre).
    const codes = new Map<string, string>();
    for (const l of locationsQuery.data ?? []) {
      const code = l.BranchCode?.trim();
      if (code && !codes.has(code)) codes.set(code, l.LocationDesc);
    }
    for (const r of live.values()) {
      const code = r.BranchCode?.trim();
      if (r.ScopeType === 'Branch' && code && !codes.has(code)) codes.set(code, 'cost centre — not an Oscar location');
    }
    for (const [code, desc] of [...codes.entries()].sort(([a], [b]) => a.localeCompare(b)))
      push(`branch:${code}`, { ScopeType: 'Branch', BranchCode: code }, code, desc, true);

    return { items, targets };
  }, [person, moduleKey, rows, regionsQuery.data, locationsQuery.data]);

  const moduleTitle = modules.find((m) => m.ModuleKey === moduleKey)?.Title ?? moduleKey;

  return (
    <div>
      <p className="text-sm text-darkGrey">
        Give one person a branch, a region or the whole company for a module, on top of whatever their role gives
        them. Every row needs a reason.
      </p>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {manualQuery.isError && <ErrorBanner message={(manualQuery.error as Error).message || 'Could not load manual access.'} />}

      <div role="tablist" aria-label="Manual access view" className="mb-3 mt-3 flex gap-2">
        {(['person', 'list'] as const).map((v) => (
          <button
            key={v}
            role="tab"
            type="button"
            aria-selected={view === v}
            onClick={() => setView(v)}
            className={`rounded-lg border px-3 py-1.5 text-sm font-semibold ${view === v ? 'border-navy bg-navy text-white' : 'border-border bg-white text-darkGrey'}`}
          >
            {v === 'person' ? 'By person' : 'All rows'}
          </button>
        ))}
      </div>

      {view === 'person' ? (
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          <div className="flex-1">
            <div className="mb-3 text-xs font-semibold text-darkGrey">
              Find a person
              <div className="mt-1 font-normal">
                <PersonPicker
                  label="Find a person"
                  value={person ? String(person.id) : ''}
                  onChange={(v, name) => setPerson(v ? { id: Number(v), name: name ?? '' } : null)}
                />
              </div>
            </div>

            <label className="mb-2 flex flex-col text-xs font-semibold text-darkGrey">
              People with manual access
              <input
                aria-label="Search people with manual access"
                value={leftQuery}
                onChange={(e) => setLeftQuery(e.target.value)}
                placeholder="Name or email"
                className={`${inputCls} mt-1 font-normal`}
              />
            </label>
            <ul className="space-y-2">
              {visiblePeople.map((p) => (
                <SelectRow
                  key={p.id}
                  primary={p.name}
                  secondary={p.email}
                  count={p.count}
                  countTone="bg-navy/10 text-navy"
                  selected={person?.id === p.id}
                  onSelect={() => setPerson({ id: p.id, name: p.name })}
                />
              ))}
              {manualQuery.isLoading && <li className="text-sm text-darkGrey/60">Loading…</li>}
              {!manualQuery.isLoading && visiblePeople.length === 0 && (
                <li className="text-sm text-darkGrey/60">
                  {term ? 'Nobody matches.' : 'Nobody has manual access yet — find a person above.'}
                </li>
              )}
            </ul>
          </div>

          <ResizablePalette>
            {person ? (
              <div className="space-y-2">
                <label className="flex flex-col text-xs font-semibold text-darkGrey">
                  Module
                  <select
                    aria-label="Module"
                    value={moduleKey}
                    onChange={(e) => setModuleKey(e.target.value)}
                    className={`${inputCls} mt-1 font-normal`}
                  >
                    {modules.map((m) => <option key={m.ModuleKey} value={m.ModuleKey}>{m.Title} ({m.ModuleKey})</option>)}
                  </select>
                </label>
                <CoveragePanel<ManualAccess>
                  title={<><span className="text-navy">{person.name}</span> — {moduleTitle}</>}
                  searchPlaceholder="Search branches and regions"
                  items={items}
                  admin={isAdmin}
                  busy={withdraw.isPending}
                  note={isAdmin && (
                    <button type="button" onClick={() => setGranting({ ScopeType: 'Branch', BranchCode: null })} className={btnSecondary}>
                      Add a cost-centre code
                    </button>
                  )}
                  renderAllocatedExtra={(r) => <Validity row={r} />}
                  onAllocate={(it) => { const t = targets.get(it.key); if (t) setGranting(t); }}
                  onRemove={setWithdrawing}
                />
              </div>
            ) : (
              <CoverageHint>Find or select a person on the left to see which branches they can see — and which they can’t.</CoverageHint>
            )}
          </ResizablePalette>
        </div>
      ) : (
        <AllRows rows={rows} admin={isAdmin} onWithdraw={setWithdrawing} loading={manualQuery.isLoading} />
      )}

      {isAdmin && person && granting && (
        <GrantDialog
          personId={person.id}
          personName={person.name}
          moduleKey={moduleKey}
          moduleTitle={moduleTitle}
          target={granting}
          onClose={() => setGranting(null)}
          onSaved={() => { setError(null); setGranting(null); void invalidate(); }}
        />
      )}

      <AlertDialog
        open={withdrawing !== null}
        onOpenChange={(o) => { if (!o) setWithdrawing(null); }}
        title="Withdraw this manual access?"
        description={
          withdrawing
            ? `${withdrawing.DisplayName} loses ${describeManualScope(withdrawing)} for ${withdrawing.ModuleTitle}, unless their role gives it to them anyway.`
            : undefined
        }
        confirmLabel="Withdraw"
        destructive
        onConfirm={() => { if (withdrawing) withdraw.mutate(withdrawing.PersonBranchRelationshipId); }}
      />
    </div>
  );
}

/** Why and until when, on an allocated row. The reason is the tooltip. */
function Validity({ row }: { row: ManualAccess }) {
  const text = row.ValidTo ? `to ${formatDate(row.ValidTo)}` : row.ValidFrom ? `from ${formatDate(row.ValidFrom)}` : null;
  return (
    <span className="shrink-0 text-[10px] text-darkGrey/60" title={row.Reason}>
      {text ?? 'ongoing'}
    </span>
  );
}

/** Allocate on the board: the reason, optional dates, and (for a typed code) the code. */
function GrantDialog({
  personId, personName, moduleKey, moduleTitle, target, onClose, onSaved,
}: {
  personId: number;
  personName: string;
  moduleKey: string;
  moduleTitle: string;
  target: GrantTarget;
  onClose: () => void;
  onSaved: () => void;
}) {
  const custom = target.ScopeType === 'Branch' && target.BranchCode === null;
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { register, handleSubmit, formState } = useForm<GrantFormData>({
    resolver: zodResolver(grantSchema),
    defaultValues: emptyGrant(custom),
  });
  const save = useMutation({
    mutationFn: (d: GrantFormData) => addManualAccess(toGrantPayload(personId, moduleKey, target, d)),
    onSuccess: onSaved,
    onError: (e) => setSubmitError(e instanceof Error ? e.message : 'Could not add the access.'),
  });
  const submit = () => void handleSubmit((d) => save.mutate(d))();
  const err = formState.errors;

  const what = target.ScopeType === 'Company'
    ? 'every branch (Company)'
    : target.ScopeType === 'Region'
      ? `every branch in ${target.RegionDesc}`
      : target.BranchCode ? `branch ${target.BranchCode}` : 'a cost-centre code';

  return (
    <AlertDialog
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={`Give ${personName} ${what}?`}
      description={`For ${moduleTitle}. This only adds to what their role gives them.`}
      confirmLabel={save.isPending ? 'Saving…' : 'Allocate'}
      confirmDisabled={save.isPending}
      closeOnConfirm={false}
      onConfirm={submit}
    >
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        {custom && (
          <label className="block text-sm font-semibold text-darkGrey">
            Branch or cost-centre code
            <input {...register('BranchCode')} placeholder="e.g. 1102" className={`${inputCls} mt-1 w-full font-normal`} />
            {err.BranchCode && <FieldError message={err.BranchCode.message} />}
          </label>
        )}
        {target.ScopeType === 'Company' && (
          <p className="text-xs font-semibold text-orange">Company gives this person every branch for the module.</p>
        )}
        <label className="block text-sm font-semibold text-darkGrey">
          Reason
          <textarea {...register('Reason')} rows={2} className={`${inputCls} mt-1 w-full font-normal`} />
          {err.Reason && <FieldError message={err.Reason.message} />}
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm font-semibold text-darkGrey">
            Valid from <span className="font-normal text-darkGrey/60">(optional)</span>
            <input type="date" {...register('ValidFrom')} className={`${inputCls} mt-1 w-full font-normal`} />
          </label>
          <label className="block text-sm font-semibold text-darkGrey">
            Valid to <span className="font-normal text-darkGrey/60">(optional, inclusive)</span>
            <input type="date" {...register('ValidTo')} className={`${inputCls} mt-1 w-full font-normal`} />
            {err.ValidTo && <FieldError message={err.ValidTo.message} />}
          </label>
        </div>
        {submitError && <p role="alert" className="text-sm text-red">{submitError}</p>}
      </form>
    </AlertDialog>
  );
}

/** Every manual row at once — reasons, dates, and (on request) the withdrawn ones. */
function AllRows({ rows, admin, loading, onWithdraw }: {
  rows: ManualAccess[]; admin: boolean; loading: boolean; onWithdraw: (r: ManualAccess) => void;
}) {
  const [search, setSearch] = useState('');
  const [showWithdrawn, setShowWithdrawn] = useState(false);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows
      .filter((r) => showWithdrawn || !r.IsDeleted)
      .filter((r) => !term
        || r.DisplayName.toLowerCase().includes(term)
        || r.PrimaryEmail.toLowerCase().includes(term)
        || r.ModuleKey.toLowerCase().includes(term)
        || (r.BranchCode ?? '').toLowerCase().includes(term));
  }, [rows, search, showWithdrawn]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search person, module or branch"
          aria-label="Search manual access"
          className={`${inputCls} w-64`}
        />
        <label className="inline-flex items-center gap-1.5 text-sm text-darkGrey">
          <input type="checkbox" checked={showWithdrawn} onChange={(e) => setShowWithdrawn(e.target.checked)} />
          Show withdrawn
        </label>
      </div>

      {loading && <p className="mt-4 text-sm text-darkGrey/70">Loading manual access…</p>}
      {!loading && (
        <div className="mt-4 overflow-x-auto rounded border border-border bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-navy text-left text-white">
              <tr>
                <th className="px-3 py-2 font-semibold">Person</th>
                <th className="px-3 py-2 font-semibold">Module</th>
                <th className="px-3 py-2 font-semibold">Scope</th>
                <th className="px-3 py-2 font-semibold">Reason</th>
                <th className="px-3 py-2 font-semibold">Valid</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                {admin && <th className="px-3 py-2 font-semibold"><span className="sr-only">Actions</span></th>}
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr>
                  <td colSpan={admin ? 7 : 6} className="px-3 py-4 text-darkGrey/70">
                    {search ? `No manual access matches “${search}”.` : 'Nobody has manual access.'}
                  </td>
                </tr>
              )}
              {visible.map((r, i) => {
                const status = manualStatus(r);
                return (
                  <tr key={r.PersonBranchRelationshipId} className={i % 2 ? 'bg-[#F4F6F9]' : 'bg-white'}>
                    <td className="px-3 py-2">
                      <span className="font-medium text-navy">{r.DisplayName}</span>
                      <span className="block text-xs text-darkGrey/70">{r.PrimaryEmail}</span>
                    </td>
                    <td className="px-3 py-2">
                      {r.ModuleTitle} <span className="font-mono text-xs text-darkGrey/60">{r.ModuleKey}</span>
                    </td>
                    <td className="px-3 py-2">{describeManualScope(r)}</td>
                    <td className="max-w-xs px-3 py-2">{r.Reason}</td>
                    <td className="whitespace-nowrap px-3 py-2">
                      {r.ValidFrom || r.ValidTo
                        ? `${r.ValidFrom ? formatDate(r.ValidFrom) : 'Always'} – ${r.ValidTo ? formatDate(r.ValidTo) : 'open'}`
                        : 'Always'}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusCls[status]}`}>{status}</span>
                    </td>
                    {admin && (
                      <td className="px-3 py-2 text-right">
                        {!r.IsDeleted && (
                          <button
                            type="button"
                            onClick={() => onWithdraw(r)}
                            className="rounded px-2 py-1 text-xs font-semibold text-red hover:underline"
                            aria-label={`Withdraw ${describeManualScope(r)} for ${r.DisplayName}`}
                          >
                            Withdraw
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
